import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  saasMySubscription, saasUploadStatement, saasStatementJob, saasListTransactions, saasUpdateTransaction,
  saasPnL, saasCheckoutStatus, saasReconcile, saasVatReturn, saasChatStreamUrl, saasVatPdfUrl,
  saasMyCompany, saasListStatements, saasStatementHistory, saasActivity, saasListCompanies,
  saasDeleteStatement, saasDeleteStatementById, saasDownloadStatementByIdUrl,
  saasCreateCashTxn, saasDeleteTransaction, saasListBanks, saasSyncMySubscription,
  paypalCaptureOrder,
} from '../api';
import { FinancialReportCard } from '../components/FinancialReportCard';
import { AccountingShell } from '../components/accounting/AccountingShell';
import { DashboardOverview } from '../components/accounting/DashboardOverview';
import { MonthSwitcher, formatMonth } from '../components/accounting/MonthSwitcher';
import { CompanySwitcher } from '../components/accounting/CompanySwitcher';
import { CompaniesManager } from '../components/accounting/CompaniesManager';
import { RenewalBanner } from '../components/accounting/RenewalBanner';
import { InvoicesPanel } from '../components/accounting/InvoicesPanel';
import { toast } from '../hooks/use-toast';
import {
  UploadCloud, TrendingUp, TrendingDown, Sparkles, FileText, ArrowUpRight, Lock,
  CheckCircle2, MessageSquare, RefreshCw, Download, X, Send, Clock, Printer, Users,
  Building2, History, Trash2, User, Plus, Banknote, Landmark,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Link as RouterLink } from 'react-router-dom';

const CATS = ['sales', 'expense', 'salary', 'vat', 'tax', 'internal_transfer', 'refund', 'other'];
const CAT_COLOR = {
  sales: 'bg-blue-100 text-blue-700', expense: 'bg-rose-100 text-rose-700', salary: 'bg-amber-100 text-amber-700',
  vat: 'bg-purple-100 text-purple-700', tax: 'bg-indigo-100 text-indigo-700',
  internal_transfer: 'bg-slate-100 text-slate-600', refund: 'bg-pink-100 text-pink-700', other: 'bg-gray-100 text-gray-600'
};
const CAT_LABEL = {
  en: { sales: 'Sales', expense: 'Expense', salary: 'Salary', vat: 'VAT', tax: 'Tax', internal_transfer: 'Internal', refund: 'Refund', other: 'Other' },
  ar: { sales: 'مبيعات', expense: 'مصروف', salary: 'رواتب', vat: 'ضريبة قيمة', tax: 'ضريبة', internal_transfer: 'تحويل داخلي', refund: 'استرداد', other: 'أخرى' }
};

const ACTION_LABEL = {
  uploaded: { en: 'Uploaded', ar: 'تم الرفع' },
  replaced: { en: 'Replaced', ar: 'تم الاستبدال' },
  deleted: { en: 'Deleted', ar: 'تم الحذف' },
  cash_added: { en: 'Cash added', ar: 'عملية نقدية' },
  cash_deleted: { en: 'Cash removed', ar: 'حذف نقدي' },
};
const ACTION_COLOR = {
  uploaded: 'bg-blue-100 text-blue-700',
  replaced: 'bg-amber-100 text-amber-700',
  deleted: 'bg-rose-100 text-rose-700',
  cash_added: 'bg-blue-100 text-blue-700',
  cash_deleted: 'bg-rose-100 text-rose-700',
};

// Build 12 recent months (YYYY-MM) descending
const buildMonths = (count = 12) => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const m = String(d.getMonth() + 1).padStart(2, '0');
    out.push(`${d.getFullYear()}-${m}`);
  }
  return out;
};

// Stable wrapper (defined outside the page so typing in inputs doesn't remount the tree)
const PageWrapper = ({ hasActive, dir, shell, children }) => hasActive
  ? <AccountingShell {...shell}>{children}</AccountingShell>
  : <div className="pt-24 pb-16 min-h-screen bg-[#f8fafc]" dir={dir}>{children}</div>;

const AccountingDashboard = () => {
  const { lang, dir } = useLang();
  const { user, logout } = useAuth();
  const [params] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [txns, setTxns] = useState([]);
  const [pnl, setPnl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [filter, setFilter] = useState('');
  const fileRef = useRef(null);
  const [company, setCompany] = useState('');
  const [companiesData, setCompaniesData] = useState(null);

  const loadCompanies = async () => {
    try {
      const d = await saasListCompanies();
      setCompaniesData(d);
      const names = (d.companies || []).map((c) => c.name);
      const saved = localStorage.getItem('iacc_active_company');
      setCompany((cur) => (names.includes(cur) ? cur : names.includes(saved) ? saved : (d.companies.find((c) => c.is_default) || d.companies[0])?.name || cur));
      return d;
    } catch { return null; }
  };
  const switchCompany = (name) => { localStorage.setItem('iacc_active_company', name); setCompany(name); };
  const [statements, setStatements] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(buildMonths(1)[0]);
  const [monthHistory, setMonthHistory] = useState([]);
  const [activity, setActivity] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatQ, setChatQ] = useState('');
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatBusy, setChatBusy] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [vatMode, setVatMode] = useState('quarter'); // 'quarter' | 'month'
  const [vatQuarter, setVatQuarter] = useState(`Q${Math.floor(new Date().getMonth() / 3) + 1}_${new Date().getFullYear()}`);
  const [cashOpen, setCashOpen] = useState(false);
  const todayStr = new Date().toISOString().slice(0, 10);
  const [cashForm, setCashForm] = useState({ date: todayStr, description: '', amount: '', category: 'sales', merchant: '' });
  const [cashSaving, setCashSaving] = useState(false);
  const [uploadBank, setUploadBank] = useState('');
  const [bankFilter, setBankFilter] = useState('all');
  const [banks, setBanks] = useState([]);
  const [section, setSection] = useState(params.get('section') || 'dashboard');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(false);

  const months = useMemo(() => {
    const base = buildMonths(12);
    const extra = [...activity.map((h) => h.month), ...statements.map((st) => st.month), ...txns.map((t) => String(t.date || '').slice(0, 7))]
      .filter((m) => /^\d{4}-\d{2}$/.test(m || '') && !base.includes(m));
    return [...new Set([...extra, ...base])].sort().reverse();
  }, [activity, statements, txns]);
  const features = useMemo(() => (sub?.limits?.features || []), [sub]);
  const has = (f) => features.includes(f);
  const monthStatements = useMemo(
    () => statements.filter((s) => s.month === selectedMonth),
    [statements, selectedMonth]
  );
  const monthStats = useMemo(() => {
    const files = monthStatements.length;
    const txns = monthStatements.reduce((s, x) => s + (x.txn_count || 0), 0);
    const bytes = monthStatements.reduce((s, x) => s + (x.size || 0), 0);
    return { files, txns, bytes };
  }, [monthStatements]);

  const load = async () => {
    try {
      const [s, comp, actv, bks] = await Promise.all([
        saasMySubscription(),
        loadCompanies(),
        saasActivity(15).catch(() => ({ activity: [] })),
        saasListBanks().catch(() => ({ banks: [] })),
      ]);
      setSub(s);
      if (!comp) { const c = await saasMyCompany().catch(() => ({ company_name: '' })); setCompany(c.company_name || ''); }
      if (company) saasListStatements(undefined, company).then((r) => setStatements(r.statements || [])).catch(() => {});
      setActivity(actv.activity || []);
      setBanks(bks.banks || []);
    } catch (e) {
      toast({ title: 'Load failed', variant: 'destructive' });
    }
  };

  useEffect(() => {
    if (!company) return;
    saasListStatements(undefined, company).then((r) => setStatements(r.statements || [])).catch(() => setStatements([]));
  }, [company]);

  const loadMonthData = async () => {
    try {
      const [t, p, hist] = await Promise.all([
        saasListTransactions({ month: selectedMonth, company_name: company || undefined }).catch(() => ({ transactions: [] })),
        saasPnL(company || undefined).catch(() => null),
        saasStatementHistory(selectedMonth).catch(() => ({ history: [] })),
      ]);
      setTxns(t.transactions || []);
      setPnl(p);
      setMonthHistory(hist.history || []);
    } catch (e) {
      toast({ title: 'Load failed', variant: 'destructive' });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (company) loadMonthData(); /* eslint-disable-next-line */ }, [selectedMonth, company]);

  useEffect(() => {
    if (params.get('checkout') === 'success' && params.get('session_id')) {
      (async () => {
        const sid = params.get('session_id');
        let activated = false;
        // 1) Try the primary polling endpoint
        try {
          const r = await saasCheckoutStatus(sid);
          activated = r?.payment_status === 'paid';
        } catch (e) { /* fall through to sync */ }
        // 2) If polling didn't confirm, silently retry via sync-my-subscription up to 3 times
        for (let i = 0; i < 3 && !activated; i++) {
          await new Promise((r) => setTimeout(r, 1500 * (i + 1)));
          try {
            await saasSyncMySubscription();
            activated = true;
          } catch (e) { /* keep retrying */ }
        }
        if (activated) {
          toast({ title: lang === 'ar' ? '🎉 تم تفعيل اشتراكك!' : '🎉 Subscription activated!' });
        } else {
          toast({
            title: lang === 'ar' ? 'الدفع تم — يتم التفعيل' : 'Payment received — activating',
            description: lang === 'ar' ? 'إذا لم تُفعَّل الصلاحيات خلال دقيقة، اضغط "لقد دفعت"' : 'If access isn\'t granted within a minute, tap "I already paid"',
          });
        }
        setTimeout(load, 1500);
      })();
    }
    // PayPal return handler
    if (params.get('paypal') === 'success' && params.get('order_id')) {
      (async () => {
        try {
          const r = await paypalCaptureOrder(params.get('order_id'));
          toast({ title: lang === 'ar' ? '🎉 تم تفعيل اشتراكك عبر PayPal!' : '🎉 PayPal subscription activated!', description: `Plan: ${r.plan?.toUpperCase()}` });
        } catch (e) {
          toast({ title: e?.response?.data?.detail || 'PayPal capture failed', variant: 'destructive' });
        }
        setTimeout(load, 1500);
      })();
    }
    // eslint-disable-next-line
  }, []);

  const runReconcile = async () => {
    setReconciling(true);
    try {
      const r = await saasReconcile(company || undefined);
      toast({ title: lang === 'ar' ? `تطابق ${r.matched} من ${r.total} عملية` : `Matched ${r.matched} of ${r.total}` });
      await loadMonthData();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
    finally { setReconciling(false); }
  };

  const downloadVat = async () => {
    try {
      const r = await saasVatReturn(vatQuarter, company || undefined);
      const blob = new Blob([JSON.stringify(r, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `VAT_Return_${vatQuarter}.json`; a.click();
      URL.revokeObjectURL(url);
      toast({ title: lang === 'ar' ? 'تم توليد الإقرار' : 'VAT return generated' });
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const askAI = async () => {
    if (!chatQ.trim()) return;
    const q = chatQ.trim();
    setChatMsgs((m) => [...m, { role: 'user', text: q }, { role: 'ai', text: '' }]);
    setChatQ(''); setChatBusy(true);
    try {
      const res = await fetch(saasChatStreamUrl(), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, company_name: company || null }),
      });
      if (!res.ok || !res.body) throw new Error(await res.text());
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const p of parts) {
          if (!p.startsWith('data:')) continue;
          const raw = p.slice(5).trim();
          if (raw === '[DONE]') continue;
          try {
            const obj = JSON.parse(raw);
            if (obj.chunk) {
              setChatMsgs((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: 'ai', text: (copy[copy.length - 1].text || '') + obj.chunk };
                return copy;
              });
            }
          } catch { /* ignore parse error */ }
        }
      }
    } catch (e) {
      setChatMsgs((m) => {
        const copy = [...m]; copy[copy.length - 1] = { role: 'ai', text: lang === 'ar' ? 'تعذر الحصول على إجابة.' : 'AI unavailable.' }; return copy;
      });
    } finally { setChatBusy(false); }
  };

  const downloadVatPdf = () => {
    window.open(saasVatPdfUrl(vatQuarter, company || undefined), '_blank');
  };

  const trialDaysLeft = sub?.status === 'trialing' && sub?.renewal_at
    ? Math.max(0, Math.ceil((new Date(sub.renewal_at) - new Date()) / 86400000))
    : null;

  const onUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast({ title: lang === 'ar' ? 'اختر ملفاً' : 'Choose a file', variant: 'destructive' }); return; }
    if (!selectedMonth) { toast({ title: lang === 'ar' ? 'اختر الشهر' : 'Choose a month', variant: 'destructive' }); return; }
    setUploading(true);
    try {
      let r = await saasUploadStatement(file, selectedMonth, uploadBank || '', company || '');
      if (r.job_id) {
        setUploadProgress({ done: 0, total: r.total });
        // Large statement — classification runs in the background; poll until done
        for (;;) {
          await new Promise((res) => setTimeout(res, 2500));
          const job = await saasStatementJob(r.job_id);
          if (job.progress) setUploadProgress(job.progress);
          if (job.status === 'done') { r = { ...job.result, skipped_duplicates: r.skipped_duplicates }; break; }
          if (job.status === 'failed') throw new Error(job.error || 'Processing failed');
        }
      }
      const sk = r.skipped_duplicates || 0;
      const mc = r.months_count || 1;
      const multi = mc > 1;
      toast({
        title: multi
          ? (lang === 'ar' ? `تم رفع الكشف — أُضيفت ${r.count} عملية موزعة على ${mc} أشهر` : `Statement added — ${r.count} transactions across ${mc} months`)
          : (lang === 'ar' ? `تم رفع الكشف — أُضيفت ${r.count} عملية` : `Statement added — ${r.count} transactions`),
        description: [
          multi ? (r.months || []).map((x) => `${formatMonth(x.month, lang)}: ${x.count}`).join(' · ') : '',
          sk ? (lang === 'ar' ? `تم تجاهل ${sk} عملية مكررة موجودة مسبقاً` : `${sk} duplicate transaction${sk > 1 ? 's' : ''} skipped`) : '',
          r.merged_invoices ? (lang === 'ar' ? `تم ربط ${r.merged_invoices} فاتورة بعمليات البنك المطابقة (بدون تكرار)` : `${r.merged_invoices} invoice${r.merged_invoices > 1 ? 's' : ''} linked to matching bank transactions (no duplicates)`) : '',
        ].filter(Boolean).join(' — ') || undefined,
      });
      if (fileRef.current) fileRef.current.value = '';
      setUploadBank('');
      await load();
      if (r.month && r.month !== selectedMonth) setSelectedMonth(r.month); else await loadMonthData();
    } catch (e) {
      const raw = String(e?.response?.data?.detail || e?.message || 'Failed');
      let msg = raw;
      if (raw.startsWith('DUPLICATE_STATEMENT:')) {
        const prev = raw.split(':').slice(1).join(':');
        msg = lang === 'ar' ? `هذا الكشف مرفوع مسبقاً في هذا الشهر${prev ? ` (${prev})` : ''}` : `This statement was already uploaded this month${prev ? ` (${prev})` : ''}`;
      } else if (raw.startsWith('ALL_DUPLICATE_TXNS:')) {
        const n = raw.split(':')[1];
        msg = lang === 'ar' ? `كل العمليات (${n}) موجودة مسبقاً في هذا الشهر — لم يُضف شيء` : `All ${n} transactions already exist in this month — nothing added`;
      }
      toast({ title: msg, variant: 'destructive' });
    } finally { setUploading(false); setUploadProgress(null); }
  };

  const onDeleteStatement = async () => {
    if (monthStatements.length === 0) return;
    const ok = window.confirm(lang === 'ar'
      ? `حذف كل كشوف هذا الشهر (${monthStatements.length}) والمعاملات المرتبطة بها؟`
      : `Delete ALL statements for this month (${monthStatements.length}) and their transactions?`);
    if (!ok) return;
    try {
      await saasDeleteStatement(selectedMonth);
      toast({ title: lang === 'ar' ? 'تم الحذف' : 'Deleted' });
      await load(); await loadMonthData();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const onDeleteStatementById = async (st) => {
    const ok = window.confirm(lang === 'ar'
      ? `حذف الكشف "${st.filename}" وجميع معاملاته (${st.txn_count || 0})؟`
      : `Delete statement "${st.filename}" and its ${st.txn_count || 0} transactions?`);
    if (!ok) return;
    try {
      await saasDeleteStatementById(st.id);
      toast({ title: lang === 'ar' ? 'تم الحذف' : 'Deleted' });
      await load(); await loadMonthData();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const changeCategory = async (txn, cat) => {
    setTxns((prev) => prev.map((t) => t.id === txn.id ? { ...t, category: cat } : t));
    try { await saasUpdateTransaction(txn.id, { category: cat }); loadMonthData(); }
    catch { toast({ title: 'Failed', variant: 'destructive' }); loadMonthData(); }
  };

  const saveCash = async () => {
    const amt = parseFloat(cashForm.amount);
    if (!cashForm.description.trim() || !Number.isFinite(amt) || amt === 0) {
      toast({ title: lang === 'ar' ? 'أدخل وصف ومبلغ صحيح' : 'Enter description and non-zero amount', variant: 'destructive' });
      return;
    }
    setCashSaving(true);
    try {
      await saasCreateCashTxn({
        date: cashForm.date,
        description: cashForm.description.trim(),
        amount: amt,
        category: cashForm.category,
        merchant: cashForm.merchant || '',
      });
      toast({ title: lang === 'ar' ? 'تمت إضافة العملية النقدية' : 'Cash transaction added' });
      setCashOpen(false);
      setCashForm({ date: todayStr, description: '', amount: '', category: 'sales', merchant: '' });
      // Jump to the month of the newly-added txn
      const m = cashForm.date.slice(0, 7);
      if (m !== selectedMonth) setSelectedMonth(m); else { await load(); await loadMonthData(); }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed';
      toast({ title: msg, variant: 'destructive' });
    } finally { setCashSaving(false); }
  };

  const deleteCash = async (txn) => {
    if (txn.source !== 'cash') return;
    const ok = window.confirm(lang === 'ar' ? 'حذف هذه العملية النقدية؟' : 'Delete this cash transaction?');
    if (!ok) return;
    try {
      await saasDeleteTransaction(txn.id);
      toast({ title: lang === 'ar' ? 'تم الحذف' : 'Deleted' });
      await load(); await loadMonthData();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed';
      toast({ title: msg, variant: 'destructive' });
    }
  };

  const filtered = txns.filter((t) => {
    if (filter && t.category !== filter) return false;
    if (bankFilter && bankFilter !== 'all') {
      if (bankFilter === '__cash__') return t.source === 'cash';
      if ((t.bank_name || '') !== bankFilter) return false;
    }
    return true;
  });
  const hasActive = sub && (sub.status === 'active' || sub.status === 'trialing');
  const sectionTxns = filtered.filter((t) => {
    if (section === 'sales' && t.category !== 'sales') return false;
    if (section === 'purchases' && t.category !== 'expense') return false;
    if (search && !`${t.description || ''} ${t.merchant || ''} ${t.category || ''} ${t.bank_name || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const setupProgress = Math.round(([!!company, statements.length > 0, txns.length > 0, !!pnl, txns.length > 0 && txns.every((t) => t.category && t.category !== 'other')].filter(Boolean).length / 5) * 100);
  const reviewCount = txns.filter((t) => !t.category || t.category === 'other').length;
  const goReview = () => { setSection('accounting'); setFilter('other'); };
  const shellProps = { lang, user, section, setSection, progress: setupProgress, search, setSearch, alerts: reviewCount, collapsed, setCollapsed, onSignOut: logout };

  return (
    <PageWrapper hasActive={hasActive} dir={dir} shell={shellProps}>
      <div className={hasActive ? 'max-w-[1400px] mx-auto' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-10'} dir={dir}>
        {/* Header */}
        {!hasActive && (
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#2563eb] mb-2">— {lang === 'ar' ? 'المحاسبة الذكية' : 'AI Accounting'} —</div>
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl text-[#0a0a0a]">{lang === 'ar' ? 'لوحة المحاسبة' : 'Accounting Dashboard'}</h1>
            {company && (
              <div className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700" data-testid="locked-company">
                <Lock className="w-3 h-3" /> <Building2 className="w-3 h-3" />
                <span className="font-semibold">{company}</span>
                <span className="text-blue-600/70">{lang === 'ar' ? '· الشركة النشطة' : '· active company'}</span>
              </div>
            )}
          </div>
          <Link to="/accounting/pricing" data-testid="pricing-link">
            <Button className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent">
              {sub?.plan && sub.plan !== 'free'
                ? `${lang === 'ar' ? 'باقتك:' : 'Plan:'} ${sub.plan.toUpperCase()}`
                : (lang === 'ar' ? 'اشترك الآن' : 'View Plans')}
              <ArrowUpRight className="w-3.5 h-3.5 ms-1" />
            </Button>
          </Link>
        </div>
        )}

        <RenewalBanner lang={lang} sub={sub} />

        {trialDaysLeft !== null && (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-3" data-testid="trial-banner">
            <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1 text-sm text-amber-900">
              {lang === 'ar'
                ? `أنت في تجربة Growth مجانية — يتبقى ${trialDaysLeft} يوم${trialDaysLeft === 1 ? '' : 'اً'} قبل الحاجة لبطاقة`
                : `You're on a free Growth trial — ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left before card required`}
            </div>
            <RouterLink to="/accounting/pricing">
              <Button className="gold-button rounded-md h-9 px-4 text-xs" data-testid="trial-upgrade">
                {lang === 'ar' ? 'ترقية الآن' : 'Upgrade Now'}
              </Button>
            </RouterLink>
          </div>
        )}

        {!hasActive ? (
          <div className="glass-card rounded-2xl p-10 text-center">
            <Lock className="w-10 h-10 text-[#2563eb] mx-auto mb-4" />
            <h2 className="font-display text-2xl text-[#0a0a0a] mb-2">{lang === 'ar' ? 'يلزم اشتراك مفعّل' : 'Active subscription required'}</h2>
            <p className="text-sm text-[#111827]/60 mb-6">{lang === 'ar' ? 'اختر باقة تناسب حجم أعمالك لبدء تحليل كشوف الحساب البنكية بالذكاء الاصطناعي.' : 'Pick a plan to start turning bank statements into finished books with AI.'}</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/accounting/pricing"><Button className="gold-button rounded-md h-11 px-6 text-sm" data-testid="paywall-cta">{lang === 'ar' ? 'استعرض الباقات' : 'Choose a plan'} <ArrowUpRight className="w-4 h-4 ms-2" /></Button></Link>
              <Button
                onClick={async () => {
                  try {
                    const r = await saasSyncMySubscription();
                    toast({ title: lang === 'ar' ? '✅ تم تفعيل اشتراكك!' : '✅ Subscription activated!', description: `Plan: ${r.plan?.toUpperCase()}` });
                    setTimeout(() => window.location.reload(), 900);
                  } catch (e) {
                    toast({ title: e?.response?.data?.detail || 'No paid subscription found', variant: 'destructive' });
                  }
                }}
                className="ghost-button rounded-md h-11 px-5 text-sm bg-transparent"
                data-testid="sync-sub-btn"
              >
                {lang === 'ar' ? 'لقد دفعت — تحقق الآن' : 'I already paid — sync now'}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {companiesData && (companiesData.companies?.length > 1 || companiesData.max_companies > 1) && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#e5e7eb] dark:border-slate-700 bg-white/70 dark:bg-slate-900/60 px-4 py-2.5" data-testid="company-bar">
                <CompanySwitcher lang={lang} companies={companiesData.companies} value={company} onChange={switchCompany} maxCompanies={companiesData.max_companies} onManage={() => setSection('settings')} />
                <div className="text-[11px] text-[#111827]/50 dark:text-slate-400">
                  {lang === 'ar' ? `${companiesData.companies.length} / ${companiesData.max_companies >= 1e8 ? '∞' : companiesData.max_companies} شركات` : `${companiesData.companies.length} / ${companiesData.max_companies >= 1e8 ? '∞' : companiesData.max_companies} companies`}
                </div>
              </div>
            )}
            {section === 'dashboard' && (<>
              <div className="mb-5"><MonthSwitcher lang={lang} months={months} statements={statements} value={selectedMonth} onChange={setSelectedMonth} testId="dashboard-month-switcher" /></div>
              <DashboardOverview lang={lang} user={user} pnl={pnl} txns={txns} statements={statements} company={company} onReview={goReview} selectedMonth={selectedMonth} />
            </>)}
{section === 'reports' && (<>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCard label={lang === 'ar' ? 'الإيرادات' : 'Revenue'} value={pnl?.revenue || 0} accent="emerald" icon={TrendingUp} />
              <StatCard label={lang === 'ar' ? 'المصروفات' : 'Expenses'} value={pnl?.total_expense || 0} accent="rose" icon={TrendingDown} />
              <StatCard label={lang === 'ar' ? 'صافي الربح' : 'Net Profit'} value={pnl?.net_profit || 0} accent={pnl?.net_profit >= 0 ? 'emerald' : 'rose'} icon={Sparkles} />
              <StatCard label={lang === 'ar' ? 'ضريبة القيمة' : 'VAT Balance'} value={pnl?.vat_balance || 0} accent="purple" icon={FileText} />
            </div>

            <FinancialReportCard lang={lang} company={company} />

</>)}
{section === 'invoices' && <InvoicesPanel lang={lang} company={company} onPosted={() => { load(); loadMonthData(); }} />}

            {section === 'settings' && (<>
            <CompaniesManager lang={lang} data={companiesData} onChanged={loadCompanies} planLabel={sub?.plan || 'free'} />
            {/* Usage */}
            {sub?.limits && (
              <div className="mb-6 glass-card rounded-xl p-4 flex items-center gap-4">
                <CheckCircle2 className="w-5 h-5 text-[#2563eb]" />
                <div className="flex-1">
                  <div className="text-xs text-[#111827]/60">
                    {lang === 'ar'
                      ? `الاستخدام هذا الشهر: ${sub.usage?.txn_this_month || 0} / ${sub.limits.max_txn >= 10 ** 8 ? '∞' : sub.limits.max_txn} معاملة`
                      : `This month: ${sub.usage?.txn_this_month || 0} / ${sub.limits.max_txn >= 10 ** 8 ? '∞' : sub.limits.max_txn} transactions`}
                  </div>
                </div>
                {sub.renewal_at && <div className="text-[10px] text-[#111827]/40">{lang === 'ar' ? 'يتجدد في' : 'Renews'}: {new Date(sub.renewal_at).toLocaleDateString()}</div>}
              </div>
            )}

</>)}
{(section === 'accounting' || section === 'reports' || section === 'sales' || section === 'purchases') && (<>
            {/* Month selector + Actions */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <MonthSwitcher lang={lang} months={months} statements={statements} value={selectedMonth} onChange={setSelectedMonth} />
              <div className="sm:ms-auto w-full sm:w-auto flex items-center gap-2 flex-wrap">
                {has('bank_reconciliation') && (
                  <Button onClick={runReconcile} disabled={reconciling} className="ghost-button rounded-md h-9 px-3 text-xs bg-transparent" data-testid="reconcile-btn">
                    <RefreshCw className={`w-3.5 h-3.5 me-1 ${reconciling ? 'animate-spin' : ''}`} /> {lang === 'ar' ? 'مطابقة تلقائية' : 'Reconcile'}
                  </Button>
                )}
                {has('vat_return') && (
                  <div className="flex items-center gap-1 rounded-md border border-[#e5e7eb] bg-white h-9 px-1.5">
                    <button
                      onClick={() => {
                        if (vatMode === 'quarter') {
                          setVatMode('month');
                          setVatQuarter(selectedMonth || new Date().toISOString().slice(0, 7));
                        } else {
                          setVatMode('quarter');
                          setVatQuarter(`Q${Math.floor(new Date().getMonth() / 3) + 1}_${new Date().getFullYear()}`);
                        }
                      }}
                      className="h-6 px-1.5 text-[10px] font-bold uppercase tracking-widest rounded bg-[#f8fafc] text-[#2563eb] border border-[#e5e7eb] hover:bg-[#2563eb]/10"
                      title={lang === 'ar' ? 'تبديل بين شهري وربع سنوي' : 'Toggle Monthly / Quarterly'}
                      data-testid="vat-mode-toggle"
                    >
                      {vatMode === 'quarter' ? 'Q' : 'M'}
                    </button>
                    <input
                      value={vatQuarter}
                      onChange={(e) => setVatQuarter(e.target.value)}
                      className="w-24 sm:w-28 h-full bg-transparent text-xs focus:outline-none text-center font-mono"
                      placeholder={vatMode === 'quarter' ? 'Q1_2026' : '2026-02'}
                      data-testid="vat-quarter"
                    />
                    <Button onClick={downloadVat} className="h-7 px-2 text-[11px] bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded" data-testid="vat-download">
                      <Download className="w-3 h-3 me-1" /> JSON
                    </Button>
                    <Button onClick={downloadVatPdf} className="h-7 px-2 text-[11px] bg-[#0a0a0a] hover:bg-[#111827] text-white rounded" data-testid="vat-pdf">
                      <Printer className="w-3 h-3 me-1" /> PDF
                    </Button>
                  </div>
                )}
                {has('data_export') && (
                  <a href={`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/saas/export/transactions.csv?month=${encodeURIComponent(selectedMonth)}`}
                     data-testid="export-csv">
                    <Button className="ghost-button rounded-md h-9 px-3 text-xs bg-transparent">
                      <Download className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'تصدير CSV' : 'Export CSV'}
                    </Button>
                  </a>
                )}
                {has('team_seats') && (
                  <RouterLink to="/accounting/team">
                    <Button className="ghost-button rounded-md h-9 px-3 text-xs bg-transparent" data-testid="team-btn">
                      <Users className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'الفريق' : 'Team'}
                    </Button>
                  </RouterLink>
                )}
                <Button onClick={() => setChatOpen(true)} className="gold-button rounded-md h-9 px-3 text-xs" data-testid="ai-chat-btn">
                  <MessageSquare className="w-3.5 h-3.5 me-1" /> <span className="hidden sm:inline">{lang === 'ar' ? 'اسأل المحاسب الذكي' : 'Ask AI'}</span><span className="sm:hidden">AI</span>
                </Button>
              </div>
            </div>

</>)}
{section === 'banking' && (<>
            <div className="mb-5" data-testid="banking-month-bar"><MonthSwitcher lang={lang} months={months} statements={statements} value={selectedMonth} onChange={setSelectedMonth} testId="banking-month-switcher" /></div>
            {/* Upload / Manage panel */}
            <div className="glass-card rounded-2xl p-6 md:p-8 mb-8">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <UploadCloud className="w-6 h-6 text-[#2563eb]" />
                  <div>
                    <h3 className="font-display text-xl text-[#0a0a0a]">
                      {monthStats.files > 0
                        ? (lang === 'ar' ? `كشوف الشهر (${monthStats.files})` : `Statements this month (${monthStats.files})`)
                        : (lang === 'ar' ? 'رفع كشف حساب بنكي' : 'Upload Bank Statement')}
                    </h3>
                    <div className="text-xs text-[#111827]/50 mt-1">
                      {formatMonth(selectedMonth, lang)}
                      {monthStats.files > 0 && (
                        <span className="ms-2 text-blue-700/70">· {monthStats.txns} {lang === 'ar' ? 'عملية' : 'txns'} · {(monthStats.bytes / 1024).toFixed(1)} KB</span>
                      )}
                    </div>
                  </div>
                </div>
                {monthStats.files > 0 && (
                  <Button onClick={onDeleteStatement} className="ghost-button rounded-md h-9 px-3 text-xs bg-transparent border-rose-400 text-rose-500" data-testid="delete-all-statements">
                    <Trash2 className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'حذف الكل' : 'Delete all'}
                  </Button>
                )}
              </div>

              {monthStatements.length > 0 && (
                <ul className="mb-4 space-y-2" data-testid="statements-list">
                  {monthStatements.map((st) => (
                    <li key={st.id} className="flex flex-wrap items-center gap-3 text-xs bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2 text-blue-900" data-testid={`stmt-${st.id}`}>
                      <FileText className="w-3.5 h-3.5" />
                      <span className="font-semibold truncate max-w-[240px]" title={st.filename}>{st.filename}</span>
                      {st.bank_name && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200 font-bold uppercase tracking-widest inline-flex items-center gap-1" data-testid={`bank-${st.id}`}>
                          <Landmark className="w-2.5 h-2.5" /> {st.bank_name}
                        </span>
                      )}
                      <span className="text-blue-700/70">· {(st.size / 1024).toFixed(1)} KB</span>
                      <span className="text-blue-700/70">· {st.txn_count || 0} {lang === 'ar' ? 'عملية' : 'txns'}</span>
                      <span className="text-blue-700/70">· {lang === 'ar' ? 'رُفع بواسطة' : 'by'} {st.uploaded_by_name}</span>
                      <span className="text-blue-700/70">· {new Date(st.created_at).toLocaleString()}</span>
                      <div className="ms-auto flex items-center gap-1">
                        <a href={saasDownloadStatementByIdUrl(st.id)} target="_blank" rel="noreferrer" data-testid={`dl-${st.id}`}>
                          <Button className="ghost-button rounded-md h-7 px-2 text-[11px] bg-transparent">
                            <Download className="w-3 h-3 me-1" /> {lang === 'ar' ? 'تحميل' : 'Download'}
                          </Button>
                        </a>
                        <Button onClick={() => onDeleteStatementById(st)} className="ghost-button rounded-md h-7 px-2 text-[11px] bg-transparent border-rose-400 text-rose-500" data-testid={`del-${st.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="grid sm:grid-cols-4 gap-3">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.pdf"
                  data-testid="upload-file"
                  className="sm:col-span-2 h-11 rounded-md border border-[#e5e7eb] bg-white px-3 text-sm file:mr-3 file:rounded file:border-0 file:bg-[#2563eb]/10 file:text-[#2563eb] file:px-3 file:py-1.5 file:text-xs file:font-semibold"
                />
                <input
                  type="text"
                  list="uae-banks"
                  value={uploadBank}
                  onChange={(e) => setUploadBank(e.target.value)}
                  placeholder={lang === 'ar' ? 'اسم البنك (اختياري)' : 'Bank name (optional)'}
                  className="h-11 rounded-md border border-[#e5e7eb] bg-white px-3 text-sm placeholder:text-[#111827]/40"
                  data-testid="upload-bank"
                />
                <datalist id="uae-banks">
                  <option value="ADCB" />
                  <option value="ENBD" />
                  <option value="Wio" />
                  <option value="Mashreq" />
                  <option value="RAK Bank" />
                  <option value="CBD" />
                  <option value="HSBC" />
                  <option value="FAB" />
                  <option value="ADIB" />
                  <option value="DIB" />
                  <option value="Citi" />
                </datalist>
                <Button onClick={onUpload} disabled={uploading} className="gold-button rounded-md h-11 text-sm" data-testid="upload-submit">
                  {uploading
                    ? (uploadProgress
                      ? (lang === 'ar' ? `جاري التصنيف… ${uploadProgress.done}/${uploadProgress.total}` : `AI classifying… ${uploadProgress.done}/${uploadProgress.total}`)
                      : (lang === 'ar' ? 'جاري التصنيف…' : 'AI classifying…'))
                    : monthStats.files > 0
                      ? (<><UploadCloud className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'إضافة كشف آخر' : 'Add another'}</>)
                      : (lang === 'ar' ? 'رفع وتصنيف' : 'Upload & Classify')}
                </Button>
              </div>
              <div className="mt-3 text-[11px] text-[#111827]/50" data-testid="upload-help-text">
                {lang === 'ar'
                  ? 'يدعم CSV و PDF. الكشوف التي تغطي عدة أشهر تُفرز تلقائياً حسب تاريخ كل عملية. ضع اسم البنك لتصنيف المعاملات حسب مصدرها لاحقاً.'
                  : 'Supports CSV & PDF. Statements spanning several months are split automatically by transaction date. Tag with a bank name to filter transactions by their source later.'}
              </div>
            </div>

            {/* Month history */}
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="glass-card rounded-2xl p-5" data-testid="month-history">
                <div className="flex items-center gap-2 mb-3">
                  <History className="w-4 h-4 text-[#2563eb]" />
                  <div className="text-xs uppercase tracking-widest text-[#2563eb] font-bold">
                    {lang === 'ar' ? `سجل هذا الشهر · ${formatMonth(selectedMonth, lang)}` : `History · ${formatMonth(selectedMonth, lang)}`}
                  </div>
                </div>
                {monthHistory.length === 0
                  ? <div className="text-xs text-[#111827]/40 py-6 text-center">{lang === 'ar' ? 'لا تعديلات بعد' : 'No activity yet for this month'}</div>
                  : (
                    <ul className="space-y-2 max-h-56 overflow-y-auto pr-1">
                      {monthHistory.map((h) => (
                        <li key={h.id} className="flex items-start gap-2 text-xs border-b border-[#e5e7eb] pb-2 last:border-0">
                          <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold ${ACTION_COLOR[h.action] || 'bg-gray-100 text-gray-600'}`}>
                            {ACTION_LABEL[h.action]?.[lang] || h.action}
                          </span>
                          <div className="flex-1">
                            <div className="text-[#0a0a0a]">
                              <User className="w-3 h-3 inline me-1" />
                              <span className="font-semibold">{h.actor_name}</span>
                              {h.meta?.filename && <span className="text-[#111827]/60"> · {h.meta.filename}</span>}
                            </div>
                            <div className="text-[10px] text-[#111827]/50">{new Date(h.created_at).toLocaleString()}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
              </div>

              <div className="glass-card rounded-2xl p-5" data-testid="activity-log">
                <div className="flex items-center gap-2 mb-3">
                  <History className="w-4 h-4 text-[#2563eb]" />
                  <div className="text-xs uppercase tracking-widest text-[#2563eb] font-bold">
                    {lang === 'ar' ? 'آخر التعديلات (جميع الأشهر)' : 'Recent Activity (all months)'}
                  </div>
                </div>
                {activity.length === 0
                  ? <div className="text-xs text-[#111827]/40 py-6 text-center">{lang === 'ar' ? 'لا نشاط بعد' : 'No activity yet'}</div>
                  : (
                    <ul className="space-y-2 max-h-56 overflow-y-auto pr-1">
                      {activity.map((h) => (
                        <li key={h.id} className="flex items-start gap-2 text-xs border-b border-[#e5e7eb] pb-2 last:border-0">
                          <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold ${ACTION_COLOR[h.action] || 'bg-gray-100 text-gray-600'}`}>
                            {ACTION_LABEL[h.action]?.[lang] || h.action}
                          </span>
                          <div className="flex-1">
                            <div className="text-[#0a0a0a]">
                              <span className="font-semibold">{h.actor_name}</span>
                              <span className="text-[#111827]/60"> · {formatMonth(h.month, lang)}</span>
                            </div>
                            <div className="text-[10px] text-[#111827]/50">{new Date(h.created_at).toLocaleString()}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
              </div>
            </div>

</>)}
{(section === 'accounting' || section === 'sales' || section === 'purchases') && (<>
            {/* Filter */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className="text-sm text-[#111827]/60">{lang === 'ar' ? 'فلترة:' : 'Filter:'}</div>
              <Select value={filter || 'all'} onValueChange={(v) => setFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-40 h-9 bg-white border-[#e5e7eb]" data-testid="cat-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{lang === 'ar' ? 'كل التصنيفات' : 'All categories'}</SelectItem>
                  {CATS.map((c) => <SelectItem key={c} value={c}>{CAT_LABEL[lang][c]}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={bankFilter} onValueChange={setBankFilter}>
                <SelectTrigger className="w-40 h-9 bg-white border-[#e5e7eb]" data-testid="bank-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{lang === 'ar' ? 'كل البنوك' : 'All banks'}</SelectItem>
                  <SelectItem value="__cash__">{lang === 'ar' ? 'نقدي فقط' : 'Cash only'}</SelectItem>
                  {banks.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={() => setCashOpen(true)} className="gold-button rounded-md h-9 px-3 text-xs" data-testid="add-cash-btn">
                <Plus className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'إضافة عملية نقدية' : 'Add Cash Transaction'}
              </Button>
              <div className="text-xs text-[#111827]/50 ms-auto">{sectionTxns.length} {lang === 'ar' ? 'عملية' : 'transactions'}</div>
            </div>

            {/* Transactions */}
            <div className="glass-card rounded-2xl overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-[#f8fafc]">
                  <tr>
                    <th className="p-3 text-start text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'التاريخ' : 'Date'}</th>
                    <th className="p-3 text-start text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'الوصف' : 'Description'}</th>
                    <th className="p-3 text-start text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'التصنيف' : 'Category'}</th>
                    <th className="p-3 text-end text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'المبلغ' : 'Amount'}</th>
                    <th className="p-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {sectionTxns.length === 0 ? (
                    <tr><td colSpan="5" className="p-10 text-center text-[#111827]/40 text-xs">{lang === 'ar' ? 'لا معاملات لهذا الشهر — ارفع كشفاً أو أضف عملية نقدية لتبدأ.' : 'No transactions for this month — upload a statement or add a cash transaction to get started.'}</td></tr>
                  ) : sectionTxns.map((t) => (
                    <tr key={t.id} className="border-t border-[#e5e7eb] hover:bg-[#f8fafc]" data-testid={`txn-${t.id}`}>
                      <td className="p-3 text-[#111827]/70 whitespace-nowrap">{t.date || '—'}</td>
                      <td className="p-3 text-[#0a0a0a] max-w-md truncate" title={t.description}>
                        {t.merchant || t.description}
                        {t.source === 'cash' && (
                          <span className="ms-2 text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 uppercase tracking-widest font-bold inline-flex items-center gap-0.5" data-testid={`cash-badge-${t.id}`}>
                            <Banknote className="w-2.5 h-2.5" /> {lang === 'ar' ? 'نقدي' : 'Cash'}
                          </span>
                        )}
                        {t.source === 'invoice' && (
                          <span className="ms-2 text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 uppercase tracking-widest font-bold inline-flex items-center gap-0.5" data-testid={`invoice-badge-${t.id}`} title={t.invoice_number || ''}>
                            {lang === 'ar' ? 'فاتورة' : 'Invoice'}
                          </span>
                        )}
                        {t.invoice_id && t.source !== 'invoice' && (
                          <span className="ms-2 text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 uppercase tracking-widest font-bold" data-testid={`invoice-linked-badge-${t.id}`} title={t.invoice_number || ''}>
                            {lang === 'ar' ? 'مرتبط بفاتورة' : 'Invoice linked'}
                          </span>
                        )}
                        {t.bank_name && (
                          <span className="ms-2 text-[9px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 uppercase tracking-widest font-bold inline-flex items-center gap-0.5" data-testid={`bank-tag-${t.id}`}>
                            <Landmark className="w-2.5 h-2.5" /> {t.bank_name}
                          </span>
                        )}
                        {(t.matched_receipt_id || t.matched_application_id) && (
                          <span className="ms-2 text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 uppercase tracking-widest font-bold" title={t.matched_receipt_id || t.matched_application_id}>
                            {lang === 'ar' ? 'مطابق' : 'Matched'}
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <Select value={t.category} onValueChange={(v) => changeCategory(t, v)}>
                          <SelectTrigger className={`w-40 h-8 border-none text-xs px-2 ${CAT_COLOR[t.category] || 'bg-gray-100'}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{CAT_LABEL[lang][c]}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className={`p-3 text-end font-mono ${t.amount >= 0 ? 'text-[#2563eb]' : 'text-rose-600'}`}>{Number(t.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="p-3 text-end">
                        {t.source === 'cash' ? (
                          <button onClick={() => deleteCash(t)} className="text-rose-500 hover:text-rose-700 transition-colors" title={lang === 'ar' ? 'حذف' : 'Delete'} data-testid={`del-cash-${t.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
</>)}
          </>
        )}

        {/* Add Cash Transaction Dialog */}
        <Dialog open={cashOpen} onOpenChange={(o) => !o && setCashOpen(false)}>
          <DialogContent className="bg-white border-[#e5e7eb] text-[#0a0a0a] max-w-lg" data-testid="cash-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#0a0a0a] font-display text-2xl flex items-center gap-2">
                <Banknote className="w-5 h-5 text-blue-600" />
                {lang === 'ar' ? 'إضافة عملية نقدية' : 'Add Cash Transaction'}
              </DialogTitle>
              <div className="text-xs text-[#111827]/60 mt-1">
                {lang === 'ar' ? 'أضف مبلغاً نقدياً لم يمر عبر البنك (استخدم قيمة سالبة للمصروفات).' : 'Add a cash amount that did not go through the bank (use a negative value for expenses).'}
              </div>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{lang === 'ar' ? 'التاريخ' : 'Date'}</Label>
                  <Input type="date" value={cashForm.date} onChange={(e) => setCashForm({ ...cashForm, date: e.target.value })} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" data-testid="cash-date" />
                </div>
                <div>
                  <Label className="text-xs">{lang === 'ar' ? 'المبلغ (درهم)' : 'Amount (AED)'}</Label>
                  <Input type="number" step="0.01" value={cashForm.amount} onChange={(e) => setCashForm({ ...cashForm, amount: e.target.value })} placeholder="0.00" className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" data-testid="cash-amount" />
                </div>
              </div>
              <div>
                <Label className="text-xs">{lang === 'ar' ? 'الوصف' : 'Description'}</Label>
                <Input value={cashForm.description} onChange={(e) => setCashForm({ ...cashForm, description: e.target.value })} placeholder={lang === 'ar' ? 'مثال: دفع نقدي لعميل' : 'e.g. Cash payment from client'} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" data-testid="cash-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{lang === 'ar' ? 'التصنيف' : 'Category'}</Label>
                  <Select value={cashForm.category} onValueChange={(v) => setCashForm({ ...cashForm, category: v })}>
                    <SelectTrigger className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" data-testid="cash-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{CAT_LABEL[lang][c]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{lang === 'ar' ? 'الجهة (اختياري)' : 'Merchant (optional)'}</Label>
                  <Input value={cashForm.merchant} onChange={(e) => setCashForm({ ...cashForm, merchant: e.target.value })} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" data-testid="cash-merchant" />
                </div>
              </div>
              <div className="text-[10px] text-[#111827]/50 bg-[#f8fafc] border border-[#e5e7eb] rounded-md p-2">
                {lang === 'ar' ? 'تلميح: للمصروفات استخدم قيمة سالبة (مثلاً -500).' : 'Tip: use a negative amount for expenses (e.g. -500).'}
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setCashOpen(false)} className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent">
                {lang === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button onClick={saveCash} disabled={cashSaving} className="gold-button rounded-md h-10 px-5 text-xs" data-testid="cash-save">
                <Plus className="w-3.5 h-3.5 me-1" /> {cashSaving ? '…' : (lang === 'ar' ? 'إضافة' : 'Add')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Chat Drawer */}
        {chatOpen && (
          <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-end pointer-events-none" data-testid="ai-chat-drawer">
            <div onClick={() => setChatOpen(false)} className="absolute inset-0 bg-black/20 pointer-events-auto" />
            <div className="relative w-full md:w-[440px] h-[85vh] md:h-[80vh] md:me-8 bg-white rounded-t-2xl md:rounded-2xl border border-[#e5e7eb] shadow-2xl flex flex-col pointer-events-auto">
              <div className="p-4 border-b border-[#e5e7eb] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#2563eb] text-white flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
                  <div>
                    <div className="font-display text-base text-[#0a0a0a]">{lang === 'ar' ? 'المحاسب الذكي' : 'AI Accountant'}</div>
                    <div className="text-[10px] text-[#111827]/50">Claude Sonnet 5 · {company || (lang === 'ar' ? 'شركتي' : 'My company')}</div>
                  </div>
                </div>
                <button onClick={() => setChatOpen(false)} className="text-[#111827]/40 hover:text-[#111827]"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatMsgs.length === 0 && (
                  <div className="text-center py-8 space-y-3">
                    <div className="text-xs text-[#111827]/60">{lang === 'ar' ? 'اسألني عن أرقام شركتك:' : 'Ask me about your books:'}</div>
                    {[
                      lang === 'ar' ? 'كم كانت مبيعات هذا الشهر؟' : 'How much did I earn this month?',
                      lang === 'ar' ? 'ما إجمالي الرواتب المدفوعة؟' : 'What are my top 3 expenses?',
                      lang === 'ar' ? 'كم رصيد VAT المستحق؟' : 'How much VAT do I owe?',
                    ].map((s) => (
                      <button key={s} onClick={() => setChatQ(s)} className="block w-full text-start px-3 py-2 rounded-lg border border-[#e5e7eb] hover:border-[#2563eb] hover:bg-[#f8fafc] text-xs text-[#111827]/80">{s}</button>
                    ))}
                  </div>
                )}
                {chatMsgs.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#0a0a0a] text-white' : 'bg-[#f8fafc] border border-[#e5e7eb] text-[#0a0a0a]'}`}>{m.text}</div>
                  </div>
                ))}
                {chatBusy && <div className="text-xs text-[#111827]/40 text-center">…</div>}
              </div>
              <div className="p-3 border-t border-[#e5e7eb] flex gap-2">
                <input
                  value={chatQ}
                  onChange={(e) => setChatQ(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && askAI()}
                  placeholder={lang === 'ar' ? 'اكتب سؤالك…' : 'Ask about your books…'}
                  className="flex-1 h-10 rounded-md border border-[#e5e7eb] px-3 text-sm bg-[#f8fafc] focus:outline-none focus:border-[#2563eb]"
                  data-testid="ai-chat-input"
                />
                <Button onClick={askAI} disabled={chatBusy || !chatQ.trim()} className="gold-button rounded-md h-10 w-10 p-0" data-testid="ai-chat-send"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

const StatCard = ({ label, value, accent, icon: Icon }) => {
  const c = { emerald: 'text-[#2563eb] bg-[#2563eb]/10', rose: 'text-rose-600 bg-rose-100', purple: 'text-purple-600 bg-purple-100' }[accent] || '';
  return (
    <div className="glass-card rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-[#111827]/50">{label}</div>
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${c}`}><Icon className="w-3.5 h-3.5" /></div>
      </div>
      <div className="text-2xl font-bold text-[#0a0a0a] mt-2 tabular-nums">{Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
      <div className="text-[10px] text-[#111827]/40 mt-0.5">AED</div>
    </div>
  );
};

export default AccountingDashboard;
